<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { hasAppUpdate } from '@/lib/appUpdate'

const available = ref(false)
let timer: ReturnType<typeof setInterval> | undefined
let pending: AbortController | undefined

async function check(): Promise<void> {
  if (document.hidden || pending || available.value) return
  const request = new AbortController()
  pending = request
  const timeout = setTimeout(() => request.abort(), 10_000)
  try {
    const updated = await hasAppUpdate(__APP_VERSION__, `${import.meta.env.BASE_URL}version.json`, request.signal)
    if (!request.signal.aborted) available.value = updated
  } finally {
    clearTimeout(timeout)
    pending = undefined
  }
}

function refresh(): void {
  // Native reload preserves the current URL and existing beforeunload guards.
  window.location.reload()
}

onMounted(() => {
  if (!import.meta.env.PROD) return
  void check()
  timer = setInterval(() => { void check() }, 60_000)
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', check)
})

onBeforeUnmount(() => {
  clearInterval(timer)
  pending?.abort()
  window.removeEventListener('focus', check)
  document.removeEventListener('visibilitychange', check)
})
</script>

<template>
  <div v-if="available" class="update-notice" role="status">
    <div>
      <strong>Налична е нова версия.</strong>
      <span>Запазете промените си, преди да презаредите.</span>
    </div>
    <Button label="Презареди" icon="pi pi-refresh" size="small" @click="refresh" />
  </div>
</template>

<style scoped>
.update-notice {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: .75rem;
  padding: .75rem 1.5rem;
  color: var(--p-highlight-color);
  background: var(--p-highlight-background);
  border-bottom: 1px solid var(--p-content-border-color);
}
.update-notice > div { display: flex; flex-wrap: wrap; gap: .25rem .5rem; }
@media print { .update-notice { display: none; } }
</style>
